import { createServerFn } from "@tanstack/react-start";
import { allPrompts } from "content-collections";

export interface CuratedPromptData {
  slug: string;
  title: string;
  prompt: string;
  imageUrl: string;
  modelId: string;
  parameters: Record<string, any>;
  metaTitle: string | null;
  metaDescription: string | null;
  tags: string[];
  category: string | null;
  featured: boolean;
  sortOrder: number;
  createdAt: Date;
}

/**
 * Pre-compute and cache prompt aggregates at module load time.
 * allPrompts is build-time data from content-collections, so these
 * never change until the next deploy.
 */
const _sortedPrompts = allPrompts.map((doc) => ({
  slug: doc.slug,
  title: doc.title,
  prompt: doc.prompt,
  imageUrl: doc.imageUrl,
  modelId: doc.modelId,
  parameters: doc.parameters,
  metaTitle: doc.metaTitle || null,
  metaDescription: doc.metaDescription || null,
  tags: doc.tags,
  category: doc.category || null,
  featured: doc.featured,
  sortOrder: doc.sortOrder,
  createdAt: doc.createdAt,
}));

// Sort once: featured first, then sortOrder, then date
const sortedPromptsCache = _sortedPrompts.sort((a, b) => {
  if (a.featured !== b.featured) return a.featured ? -1 : 1;
  if (a.sortOrder !== b.sortOrder) return b.sortOrder - a.sortOrder;
  return b.createdAt.getTime() - a.createdAt.getTime();
});

// Pre-compute categories once
const categoriesCache = [
  ...new Set(sortedPromptsCache.map((p) => p.category).filter(Boolean)),
] as string[];

// Pre-compute popular tags once
const allTags = sortedPromptsCache.flatMap((p) => p.tags);
const tagCounts = allTags.reduce((acc: Record<string, number>, tag) => {
  acc[tag] = (acc[tag] || 0) + 1;
  return acc;
}, {});
const popularTagsCache = [...new Set(allTags)]
  .sort((a, b) => (tagCounts[b] || 0) - (tagCounts[a] || 0))
  .slice(0, 8);

/**
 * Server function: Get all prompts (memoized — computed once at build time)
 */
export const getAllPrompts = createServerFn({ method: "GET" }).handler(
  async () => {
    // Return cached sorted prompts — no re-computation needed
    return sortedPromptsCache;
  },
);

/**
 * Server function: Get pre-computed categories
 */
export const getPromptCategories = createServerFn({ method: "GET" }).handler(
  async () => categoriesCache,
);

/**
 * Server function: Get pre-computed popular tags
 */
export const getPromptPopularTags = createServerFn({ method: "GET" }).handler(
  async () => popularTagsCache,
);

/**
 * Server function: Get paginated prompts
 */
export const getPaginatedPrompts = createServerFn({ method: "GET" })
  .inputValidator((data: { page: number; limit: number }) => data)
  .handler(async ({ data }) => {
    const { page = 1, limit = 20 } = data;
    const total = sortedPromptsCache.length;
    const totalPages = Math.ceil(total / limit);
    const offset = (page - 1) * limit;

    return {
      data: sortedPromptsCache.slice(offset, offset + limit),
      pagination: {
        page,
        limit,
        total,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      },
    };
  });

/**
 * Server function: Get single prompt by slug
 */
export const getPromptBySlug = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  .handler(async ({ data: slug }) => {
    const allPrompts = await getAllPrompts();
    return allPrompts.find((p) => p.slug === slug) || null;
  });
